// Community strip: Stally's newest Instagram posts, resized to WebP by the ig.stallybrand.com Worker (repo ig-images/,
// S-035), which holds the Instafeed key so the page never prints it; if the feed fails, the page photos stay.
// shortcut: images that fail after the feed loaded (Worker down, SIGNING_KEY just rotated) show as broken tiles, as
// Instagram's own URLs did; if that ever happens, keep the page photos before replaceChildren and put them back from an
// img error handler.
let rail = document.getElementById('community-photos'); // replaced on shopify:section:load (theme editor)
const CONFIG = {
  endpoint: 'https://ig.stallybrand.com/feed',
  limit: 12
};
// Only images the Worker serves: its /i/{w}/<signature>/<source> route resizes what it signed and nothing else, and
// base64url segments leave no room for a second URL in the srcset.
const IMAGE_PREFIX = new URL('/i/', CONFIG.endpoint).href;
const isWorkerImage = url => typeof url === 'string' && url.startsWith(IMAGE_PREFIX)
  && /^\{w\}\/[\w-]+\/[\w-]+$/.test(url.slice(IMAGE_PREFIX.length));
// The tile widths of the CSS grid (assets/stally-rd.css: 3 columns with a 2x2 first tile up to 650 px, then 4), measured
// at 390/768/1024/1440/3840; sections/stally-rd-community.liquid gives its fallback photos the same values.
// shortcut: these media queries read the viewport while the grid switches on the .rd container, so a window 651-665 or
// 993-1007 px wide with a classic 15 px scrollbar gets sizes about 28% small (one width step lower at DPR 2); if desktop
// windows that wide matter, prefix both strings here and in the section with "auto, " so browsers that support
// sizes=auto use the lazy tile's real width.
const SIZES_BIG = '(max-width: 650px) 60vw, (max-width: 992px) 45vw, (max-width: 1440px) 32vw, 512px';
const SIZES_SMALL = '(max-width: 650px) 30vw, (max-width: 992px) 22vw, (max-width: 1440px) 16vw, 252px';
const SRC_WIDTH = 480; // the src for browsers without srcset; one of the Worker's widths
// approved screen-reader copy: reword only through the copy sheet
const ALT_SUFFIX = ", on Stally's Instagram";
const FALLBACK_ALT = 'Stally community post on Instagram';
const VIDEO_SUFFIX = ' (video)';
const NEW_TAB_NOTE = 'Opens in a new tab';
const CAPTION_CHARS = 64;
const STORE_TIME_ZONE = 'America/Chicago';
const PROFILE = 'https://www.instagram.com/stallybrand/';
const PLAY = '<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M232.4,114.49,88.32,26.35a16,16,0,0,0-16.2-.3A15.86,15.86,0,0,0,64,39.87V216.13A15.94,15.94,0,0,0,80,232a16.07,16.07,0,0,0,8.36-2.35L232.4,141.51a15.81,15.81,0,0,0,0-27Z"/></svg>';


// { posts, widths } from the Worker, which already retries Instafeed and answers 502 with no posts when it fails;
// null (after a console warning) keeps the page photos.
async function loadFeed() {
  // shortcut: a failed feed shows only in the browser console; add analytics or uptime alerting in the theme
  try {
    const res = await fetch(CONFIG.endpoint);
    const json = await res.json().catch(() => undefined);
    if (res.ok && Array.isArray(json?.posts)) {
      return { posts: json.posts, widths: Array.isArray(json.widths) ? json.widths.filter(width => Number.isInteger(width) && width > 0) : [] };
    }
    console.warn('Instagram feed:', res.ok ? 'unreadable response' : `HTTP ${res.status}`, '- keeping the page photos.');
  } catch (error) {
    // also a host error without CORS headers: the status is only in the Network panel
    console.warn('Instagram feed unreachable, blocked or unreadable - keeping the page photos.', error);
  }
  return null;
}

const isInstagramUrl = url => typeof url === 'string' && /^https:\/\/(www\.)?instagram\.com\//.test(url);

// Grapheme clusters, so a cut never splits an emoji such as ❤️ or 🇺🇸; code points where Intl.Segmenter is missing
const visibleCharacters = text => typeof Intl.Segmenter === 'function'
  ? Array.from(new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text), part => part.segment)
  : Array.from(text);

function buildAltText(post) {
  if (post.alt_text) return post.alt_text;
  const caption = (typeof post.caption === 'string' ? post.caption : '').replace(/\s+/g, ' ').trim();
  if (!caption) return FALLBACK_ALT;
  const chars = visibleCharacters(caption);
  const excerpt = chars.length > CAPTION_CHARS ? `${chars.slice(0, CAPTION_CHARS).join('').trimEnd()}…` : caption;
  return excerpt + ALT_SUFFIX; // caption first: easier to scan in a screen reader's list of links
}

const POSTED_DAY = { month: 'long', day: 'numeric' };
const POSTED_TIME = { hour: 'numeric', minute: '2-digit' };

// In the store's time zone, so every visitor gets the same names whatever their own zone.
function postedAt(post, format) {
  const seconds = Number(post.created_time);
  return Number.isFinite(seconds) && seconds > 0
    ? new Date(seconds * 1000).toLocaleString('en-US', { ...format, timeZone: STORE_TIME_ZONE })
    : '';
}

// Links to different posts need different names, and teaser posts often reuse one caption: a repeated name gets the day
// it was posted, and a name still repeated after that (same caption, same store day) gets the time as well (S-037).
// shortcut: posts with the same caption still share a name when they share the minute (or the repeated hour when daylight
// saving ends) or have no created_time; number them if that ever happens
function buildAltTexts(posts) {
  const alts = posts.map(buildAltText);
  const repeated = (names, i) => names.indexOf(names[i]) !== names.lastIndexOf(names[i]);
  const days = alts.map((alt, i) => (repeated(alts, i) ? postedAt(posts[i], POSTED_DAY) : ''));
  const dated = alts.map((alt, i) => (days[i] ? `${alt} (posted ${days[i]})` : alt));
  return dated.map((name, i) => {
    const time = days[i] && repeated(dated, i) ? postedAt(posts[i], POSTED_TIME) : '';
    return time ? `${alts[i]} (posted ${days[i]} at ${time})` : name;
  });
}

function addNote(id, text) {
  if (document.getElementById(id)) return; // one note even if the theme re-runs the script, e.g. on shopify:section:load
  const note = document.createElement('span');
  note.id = id;
  note.hidden = true; // still read out through aria-describedby
  note.textContent = text;
  rail.after(note);
}

function tile(post, alt, sizes, widths) {
  const link = document.createElement('a');
  link.href = isInstagramUrl(post.link) ? post.link : PROFILE; // third-party data: only instagram.com links
  link.target = '_blank';
  link.rel = 'noreferrer';
  link.dataset.instagramId = post.id;
  const img = document.createElement('img');
  img.loading = 'lazy'; // before src: hidden tiles (7+ on phones, 10+ wider) must not download
  img.decoding = 'async';
  const at = width => post.image.replace('{w}', String(width));
  img.sizes = sizes;
  if (widths.length) img.srcset = widths.map(width => `${at(width)} ${width}w`).join(', ');
  img.src = at(widths.length && !widths.includes(SRC_WIDTH) ? widths[0] : SRC_WIDTH);
  img.alt = post.type === 'video' ? alt + VIDEO_SUFFIX : alt; // the play badge is hidden from screen readers, so the name carries it
  img.width = 1080; // tiles are square; this only gives the intrinsic ratio
  img.height = 1080;
  img.style.objectPosition = '50% 35%';
  link.append(img);
  if (post.type === 'video') link.insertAdjacentHTML('beforeend', `<span class="feed-badge">${PLAY}</span>`);
  link.setAttribute('aria-describedby', 'feed-note-new-tab');
  return link;
}

async function fill() {
  if (!rail) return;
  try {
    const feed = await loadFeed();
    if (!feed) return;
    const posts = feed.posts.filter(post => isWorkerImage(post?.image)).slice(0, CONFIG.limit);
    if (!posts.length) {
      console.warn('Instagram feed: no posts to show - keeping the page photos.');
      return;
    }
    addNote('feed-note-new-tab', NEW_TAB_NOTE);
    const alts = buildAltTexts(posts);
    rail.replaceChildren(...posts.map((post, i) => tile(post, alts[i], i === 0 ? SIZES_BIG : SIZES_SMALL, feed.widths)));
    rail.scrollLeft = 0;
    rail.dispatchEvent(new Event('scroll'));
  } catch (error) {
    console.warn('Instagram feed unavailable - keeping the page photos.', error);
  }
}

fill();
document.addEventListener('shopify:section:load', event => {
  const fresh = event.target.querySelector('#community-photos');
  if (fresh) { rail = fresh; fill(); }
});
